import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ArrowRightLeft, Ruler } from 'lucide-react';

const UnitConverter = () => {
    const [inputValue, setInputValue] = useState("1"); // String to handle empty/leading zeros
    const [fromUnit, setFromUnit] = useState('m2');
    const [toUnit, setToUnit] = useState('v2');
    const [result, setResult] = useState(0);

    // Conversion factors relative to Square Meters (m2)
    const units = {
        'm2': { name: 'Metros Cuadrados (m²)', symbol: 'm²', factor: 1 },
        'v2': { name: 'Varas Cuadradas (v²)', symbol: 'v²', factor: 0.698896 },
        'mz': { name: 'Manzanas (Mz)', symbol: 'Mz', factor: 6988.96 },
        'ac': { name: 'Acres', symbol: 'ac', factor: 4046.86 },
        'ha': { name: 'Hectáreas (Ha)', symbol: 'Ha', factor: 10000 },
        'ft2': { name: 'Pies Cuadrados (ft²)', symbol: 'ft²', factor: 0.092903 }
    };

    const handleConvert = () => {
        const val = parseFloat(inputValue) || 0;
        const valueInMeters = val * units[fromUnit].factor;
        const convertedValue = valueInMeters / units[toUnit].factor;
        setResult(convertedValue);
    };

    // Auto-convert on change
    React.useEffect(() => {
        handleConvert();
    }, [inputValue, fromUnit, toUnit]);

    const swapUnits = () => {
        setFromUnit(toUnit);
        setToUnit(fromUnit);
    };

    const handleInputChange = (e) => {
        // Allow empty string or valid number
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d*$/.test(val)) {
            setInputValue(val);
        }
    };

    // Calculate all other units for reference
    const getAllConversions = () => {
        const val = parseFloat(inputValue) || 0;
        const valueInMeters = val * units[fromUnit].factor;
        return Object.entries(units).map(([key, unit]) => ({
            key,
            name: unit.name,
            value: valueInMeters / unit.factor
        })).filter(u => u.key !== fromUnit && u.key !== toUnit); // Exclude current selection
    };

    const otherConversions = getAllConversions();

    return (
        <div className="max-w-4xl mx-auto">
            <Card className="shadow-lg border-t-4 border-t-secondary">
                <CardHeader className="text-center pb-2">
                    <CardTitle className="text-2xl text-primary flex items-center justify-center gap-2">
                        <Ruler className="w-6 h-6" /> Conversor de Unidades Inmobiliarias
                    </CardTitle>
                    <p className="text-gray-500">Convierte fácilmente entre varas cuadradas, metros, manzanas y más.</p>
                </CardHeader>
                <CardContent className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-7 gap-4 items-center mb-8">
                        {/* FROM */}
                        <div className="md:col-span-3 space-y-2">
                            <Label className="text-gray-600">De:</Label>
                            <div className="flex gap-2">
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    className="text-lg font-semibold"
                                />
                                <Select value={fromUnit} onValueChange={setFromUnit}>
                                    <SelectTrigger className="w-[80px] md:w-[100px]">
                                        <SelectValue>{units[fromUnit].symbol}</SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(units).map(([key, unit]) => (
                                            <SelectItem key={key} value={key} className="truncate">
                                                {unit.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* SWAP ICON */}
                        <div className="md:col-span-1 flex justify-center">
                            <Button variant="ghost" size="icon" onClick={swapUnits} className="rounded-full hover:bg-gray-100">
                                <ArrowRightLeft className="w-6 h-6 text-secondary" />
                            </Button>
                        </div>

                        {/* TO */}
                        <div className="md:col-span-3 space-y-2">
                            <Label className="text-gray-600">A:</Label>
                            <div className="flex gap-2">
                                <div className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-md flex items-center text-lg font-bold text-primary overflow-hidden">
                                    {result.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                                </div>
                                <Select value={toUnit} onValueChange={setToUnit}>
                                    <SelectTrigger className="w-[80px] md:w-[100px]">
                                        <SelectValue>{units[toUnit].symbol}</SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(units).map(([key, unit]) => (
                                            <SelectItem key={key} value={key} className="truncate">
                                                {unit.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* All Conversions Grid */}
                    {inputValue && parseFloat(inputValue) > 0 && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 text-center">
                                Equivalencias para {inputValue} {units[fromUnit].name}
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {otherConversions.map((item) => (
                                    <div key={item.key} className="bg-white p-3 rounded-lg border border-gray-200 text-center shadow-sm">
                                        <div className="text-xs text-gray-500 uppercase mb-1">{item.name}</div>
                                        <div className="font-bold text-primary text-lg">
                                            {item.value.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quick Reference Table */}
                    <div className="mt-8 pt-6 border-t border-gray-100">
                        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 text-center">Referencias Comunes en El Salvador</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-center">
                            <div className="p-3 bg-blue-50 rounded-lg">
                                <span className="block font-bold text-blue-800">1 Vara Cuadrada</span>
                                <span className="text-blue-600">0.6989 m²</span>
                            </div>
                            <div className="p-3 bg-green-50 rounded-lg">
                                <span className="block font-bold text-green-800">1 Metro Cuadrado</span>
                                <span className="text-green-600">1.4308 v²</span>
                            </div>
                            <div className="p-3 bg-orange-50 rounded-lg">
                                <span className="block font-bold text-orange-800">1 Manzana</span>
                                <span className="text-orange-600">10,000 v²</span>
                            </div>
                            <div className="p-3 bg-purple-50 rounded-lg">
                                <span className="block font-bold text-purple-800">1 Hectárea</span>
                                <span className="text-purple-600">1.43 Manzanas</span>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default UnitConverter;
